import { darken, lighten } from '@mui/material';
import type { ThemeOptions } from '@mui/material';

import { black, gray, green, red, white, yellow } from '~/styles/colors';
import { hex2rgba } from '~/utils/utils';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    default: true;
    error: true;
    warning: true;
    success: true;
    outlined: true;
    text: true;
  }
}

const ButtonTheme: ThemeOptions['components'] = {
  MuiButton: {
    defaultProps: {
      fullWidth: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        fontSize: '14px',
        textTransform: 'none',
        whiteSpace: 'nowrap',
        borderRadius: '6px',
        '&.Mui-disabled': {
          backgroundColor: hex2rgba(black[400], 0.25),
          color: white,
          '& .MuiSvgIcon-root': {
            color: `${white}`,
          },
        },
        '& .MuiButton-startIcon': {
          marginLeft: '0px',
          marginRight: '4px',
        },
      },
      sizeSmall: {
        height: '26px',
      },
      sizeMedium: {
        height: '32px',
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      sizeLarge: {
        height: '36px',
      },
    },
    variants: [
      {
        props: { variant: 'default' },
        style: ({ theme: contextTheme }) => ({
          color: white,
          backgroundColor: black[1000],
          '&:hover': {
            backgroundColor: lighten(contextTheme.palette.primary.dark, 0.25),
          },
          '&:active': {
            '&:focus': {
              backgroundColor: lighten(contextTheme.palette.primary.dark, 0.1),
              boxShadow: 'none !important',
              color: darken(white, 0.12),
            },
          },
          '&:focus': {
            backgroundColor: contextTheme.palette.primary.dark,
            boxShadow: `0px 0px 0px 4px ${hex2rgba(
              contextTheme.palette.primary.dark,
              0.12,
            )} !important`,
          },
          '&.Mui-disabled': {
            backgroundColor: hex2rgba(contextTheme.palette.primary.dark, 0.25),
            color: white,
            '& .MuiSvgIcon-root': {
              color: `${white} !important`,
            },
          },
          '& .MuiButton-startIcon': {
            marginLeft: '0px',
            marginRight: '4px',
          },
        }),
      },
      {
        props: { variant: 'error' },
        style: {
          backgroundColor: red[500],
          color: white,
          '&:hover': {
            backgroundColor: red[700],
          },
          '&:focus': {
            backgroundColor: red[500],
            boxShadow: '0px 0px 0px 4px rgba(174, 23, 23, 0.26)',
          },
          '&.Mui-disabled': {
            backgroundColor: hex2rgba(red[500], 0.25),
            color: white,
            '& .MuiSvgIcon-root': {
              color: `${white}`,
            },
          },
        },
      },
      {
        props: { variant: 'warning' },
        style: {
          backgroundColor: yellow[500],
          color: black[1000],
          '&:hover': {
            backgroundColor: yellow[700],
          },
          '&:focus': {
            backgroundColor: yellow[500],
            boxShadow: '0px 0px 0px 4px rgba(255, 199, 0, 0.26)',
          },
        },
      },
      {
        props: { variant: 'success' },
        style: {
          backgroundColor: green[900],
          color: white,
          '&:hover': {
            backgroundColor: green[800],
          },
          '&:focus': {
            backgroundColor: green[900],
            boxShadow: '0px 0px 0px 4px rgba(105, 170, 102, 0.26)',
          },
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          fontWeight: 400,
          backgroundColor: white,
          border: '1px solid rgba(0, 0, 0, 0.07)',
          borderRadius: '6px',
          boxShadow: 'none !important',
          color: black[1000],
          letterSpacing: '-1%',
          textTransform: 'none',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: gray[50],
            border: '1px solid rgba(0, 0, 0, 0.07)',
          },
          '&:focus': {
            backgroundColor: white,
            boxShadow: '0px 0px 0px 4px rgba(0, 0, 0, 0.12) !important',
          },
          '&.Mui-disabled': {
            border: 'none',
            color: black[400],
            '& .MuiSvgIcon-root': {
              color: black[400],
            },
          },
        },
      },
      {
        props: { variant: 'text' },
        style: {
          color: gray[600],
          height: '32px',
          fontWeight: '400',
          letterSpacing: '-1%',
          paddingLeft: '4px',
          paddingRight: '4px',
          minWidth: '0px',
          '&:hover': {
            backgroundColor: black[50],
            color: black[1000],
          },
          '&:focus': {
            backgroundColor: white,
            boxShadow: '0px 0px 0px 4px rgba(0, 0, 0, 0.12) !important',
            color: gray[600],
            '&:hover': {
              color: gray[600],
            },
          },
          '&.MuiButton-label': {
            width: 'min-content',
          },
        },
      },
    ],
  },
};

export default ButtonTheme;
